import { Link } from 'react-router-dom'

import { addDays, type ISODate } from '@app/shared'
import { useCapturedLectures, useJournalEntries, useTasks } from '../lib/data'

// ============================================================================
// WHAT YOU TOUCHED LAST — the search field's suggestions before a query exists.
//
// THE ASK (owner, 2026-09-01): "make the search bar suggest recent inputs, like if I added a
// recent reminder ... or if the most recent thing was a transcript uploaded to show that."
//
// NO NEW ENDPOINT, and that is the whole design. Every row below comes from a DataAPI method the
// app already calls and already documents — reminders, captured lectures, journal entries — so
// nothing here is a fourth source of truth that can drift from the three real ones, and every
// item stays exactly as machine-readable as it was. A `/api/recent` that re-derived the same rows
// would be a contract to keep in sync for no new information (CLAUDE.md → build the smaller one).
//
// THE QUERIES ONLY RUN WHEN THE LIST IS OPEN, and mounting is the whole mechanism. This panel is
// rendered only while the field has focus, so its three reads start when it appears and never
// happen at all on a Today the owner did not search from. No `enabled` flag to keep in sync, and
// no change to the shared hooks (two of the three do not take one).
//
// ONE HONEST LIMITATION, stated here because it is visible in the ordering. A captured lecture
// carries THE DAY THE CLASS HAPPENED, not the moment it was uploaded (types.ts → CapturedLecture:
// "The day the class happened, not the day you upload it"). So a recording sorts by its class
// day, and a lecture uploaded today for last Tuesday's class sorts under last Tuesday. Every row
// prints its own date, so the order is always checkable against what it claims rather than being
// something the list asks you to trust. Giving the API an upload timestamp is the real fix and is
// a Worker change, not a UI one.
// ============================================================================

export interface RecentItem {
  key: string
  /** Which system it came from. Printed, never inferred from the shape of the label. */
  kind: 'Reminder' | 'Recording' | 'Journal'
  label: string
  /** The day it belongs to, and the sort key. */
  date: ISODate
  to: string
}

/** How many of each source, before the merge. Small on purpose: this is a hint, not a screen. */
const PER_SOURCE = 4
export const MAX_SUGGESTIONS = 6

/**
 * The three sources, merged newest-first.
 *
 * Sorted on `date` alone rather than a mix of timestamps and days: two of the three sources only
 * expose a day, and a sort key that is a timestamp for some rows and a day for others produces an
 * order nobody can predict. A day is the coarsest of the three, so it is the honest one.
 */
export function useRecentItems(today: ISODate | undefined): RecentItem[] {
  const { data: reminders } = useTasks({ done: false })
  const { data: lectures } = useCapturedLectures(PER_SOURCE)
  const { data: entries } = useJournalEntries(
    today ? { from: addDays(today, -13), to: today } : undefined,
  )

  const items: RecentItem[] = []

  // SORT, THEN TAKE. Each endpoint returns its own natural order -- tasks by list and due date,
  // journal entries ascending across the range -- and slicing before sorting takes the OLDEST
  // four from two of the three sources. Caught in the browser: a reminder added seconds earlier
  // never appeared, because it was fifth in an order that had nothing to do with recency.
  for (const task of [...(reminders ?? [])]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, PER_SOURCE)) {
    items.push({
      key: `task-${task.id}`,
      kind: 'Reminder',
      label: task.title,
      date: task.createdAt.slice(0, 10) as ISODate,
      to: '/tasks',
    })
  }

  for (const lecture of [...(lectures ?? [])]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, PER_SOURCE)) {
    items.push({
      key: `lecture-${lecture.id}`,
      // A recording with no words back yet is NOT called a transcript — the row would be claiming
      // something the desktop worker has not delivered.
      kind: 'Recording',
      label: lecture.title ?? `${lecture.courseCode ?? 'Unfiled'} recording`,
      date: lecture.date,
      to: `/recordings/${lecture.id}`,
    })
  }

  for (const entry of [...(entries ?? [])]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, PER_SOURCE)) {
    // An empty day is a day with no entry, and offering it would be offering nothing.
    if (entry.wordCount === 0) continue
    items.push({
      key: `journal-${entry.date}`,
      kind: 'Journal',
      label: entry.text.trim().slice(0, 80) || entry.prompt,
      date: entry.date,
      to: `/journal/${entry.date}`,
    })
  }

  return items.sort((a, b) => b.date.localeCompare(a.date)).slice(0, MAX_SUGGESTIONS)
}

/** Substring match on the label, so typing narrows the same list rather than replacing it. */
export function filterRecent(items: RecentItem[], query: string): RecentItem[] {
  const q = query.trim().toLowerCase()
  if (!q) return items
  return items.filter((item) => item.label.toLowerCase().includes(q))
}

/**
 * MOUNTED ONLY WHILE THE FIELD HAS FOCUS -- see the note at the top of the file. It fetches on
 * mount, which is what keeps the cost on the search rather than on Today.
 */
export function RecentSuggestions({
  today,
  query,
  onPick,
}: {
  today: ISODate | undefined
  query: string
  onPick: () => void
}) {
  const items = filterRecent(useRecentItems(today), query)

  return (
    // POSITIONED BY THE LAYOUT, NOT BY MEASUREMENT. lib/popover.ts exists because the app runs at
    // zoom 1.3 and getBoundingClientRect math drifts off-screen — this panel needs none of it: it
    // is a child of the search form and sits under it with `top-full`, so the browser does the
    // arithmetic and the zoom cannot be got wrong.
    <div
      role="listbox"
      aria-label="Recent items"
      className="absolute left-0 right-0 top-full z-30 mt-[6px] flex flex-col overflow-hidden rounded-tile border border-line-2 bg-surface py-[5px] shadow-card"
    >
      {items.length === 0 ? (
        <p className="px-3 py-[7px] text-[11.5px] leading-[1.45] text-microcopy text-pretty">
          {query.trim()
            ? 'Nothing recent matches. Press Enter to search everything.'
            : 'Nothing recent yet.'}
        </p>
      ) : (
        items.map((item) => (
          <Link
            key={item.key}
            to={item.to}
            role="option"
            aria-selected={false}
            // The field's blur would close this panel before a click landed, so the press is what
            // navigates and the blur is prevented from firing first.
            onMouseDown={(event) => event.preventDefault()}
            onClick={onPick}
            className="flex items-baseline gap-[9px] px-3 py-[7px] text-[12.5px] leading-[1.35] text-ink-2 transition-colors duration-[var(--dur-hover)] ease-[var(--ease-ink)] hover:bg-surface-2"
          >
            <span className="w-[62px] flex-none text-[10px] font-semibold uppercase leading-none tracking-[0.05em] text-faint">
              {item.kind}
            </span>
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            <span className="flex-none text-[10.5px] leading-none text-microcopy">{item.date}</span>
          </Link>
        ))
      )}
    </div>
  )
}
